import * as React from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';

export interface StatusOption {
  value: string;
  label: string;
  /** dot + tag color, e.g. '#16a34a'; defaults to neutral gray */
  color?: string;
}

interface AdminStatusSelectProps {
  options: StatusOption[];
  value?: string | null;
  /** Endpoint receiving the change (PATCH by default) */
  updateUrl: string;
  /** Request param carrying the new value, e.g. 'status' or 'stage' */
  paramName: string;
  method?: 'PATCH' | 'POST';
  csrfToken: string;
  placeholder?: string;
  minWidth?: number;
}

const DOT_STYLES: StylesConfig<StatusOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 31,
    fontSize: 13,
    borderColor: state.isFocused ? '#0f4b89' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(15, 75, 137, 0.15)' : 'none',
    '&:hover': { borderColor: '#0f4b89' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base, { data, isFocused, isSelected }) => ({
    ...base,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: isSelected ? 'rgba(15, 75, 137, 0.1)' : isFocused ? '#f3f4f6' : 'white',
    color: '#111827',
    ':before': {
      content: '" "',
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 999,
      backgroundColor: data.color || '#9ca3af',
      flexShrink: 0,
    },
  }),
  singleValue: (base, { data }) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    ':before': {
      content: '" "',
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 999,
      backgroundColor: data.color || '#9ca3af',
      flexShrink: 0,
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

/**
 * AdminStatusSelect - the one status/stage control for admin lists.
 *
 * Color-coded, searchable, optimistic: the new value shows immediately,
 * PATCHes the endpoint, and rolls back with an inline error if the server
 * rejects it. Used for applicant status and sourcing-project stages.
 */
export const AdminStatusSelect: React.FC<AdminStatusSelectProps> = ({
  options,
  value,
  updateUrl,
  paramName,
  method = 'PATCH',
  csrfToken,
  placeholder = 'Set status…',
  minWidth = 170,
}) => {
  const [current, setCurrent] = React.useState<string | null>(value ?? null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const selected = options.find((option) => option.value === current) || null;

  const handleChange = async (next: SingleValue<StatusOption>) => {
    if (!next || next.value === current) return;
    const previous = current;
    setCurrent(next.value);
    setSaving(true);
    setError(null);
    try {
      const resp = await fetch(updateUrl, {
        method,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: JSON.stringify({ [paramName]: next.value }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error(body?.errors?.join(', ') || `Update failed (${resp.status})`);
      }
    } catch (e: any) {
      setCurrent(previous);
      setError(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minWidth }} data-testid="admin-status-select">
      <Select<StatusOption, false>
        options={options}
        value={selected}
        onChange={handleChange}
        isDisabled={saving}
        isSearchable
        placeholder={placeholder}
        styles={DOT_STYLES}
        classNamePrefix="admin-status-select"
        menuPortalTarget={typeof document === 'undefined' ? null : document.body}
        menuPlacement="auto"
      />
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default AdminStatusSelect;
